// IE 2026 10 28 / ODOO-5800 (заказ Игоря: вкладка History of changes в отчёте, вариант A)
// ВКЛАДКА «HISTORY OF CHANGES» ОТЧЁТА (AttrPmHistory.vue) — чистая логика без store: модель ответа бэка, раскладка пузырей «дорожка × день»,
// отбор строк таблицы изменений, запрос дня и правка параметров отчёта для Open / Compare.
// Данные кладёт бэк (common_modules/pm_history.py, контракт version 1) в record._doc.pm_history ленивым методом pm_history виджета widget_REPORT2:
//   { version, state: 'ok' | 'off' | 'no_archives' | 'error: …', process_model, current_version, lanes: [{ key, title }], selected,
//     days: [{ date, pm_id, title, version, current, complete, vs, changes, objects, magnitude, lanes: { key: { n, q } }, open: { process_model },
//              compare: { process_model, compare_process_model } | null, error? }],
//     changes: [{ date, object, object_title, node, subject, level, country, measure, lane, month, before, after, delta, reason }], filter, truncated }
// IE 2026 10 28 / ODOO-5800 (заказ Игоря: вкладка History of changes в отчёте, вариант A): PR #552 бэка добавил filter.{ from_report, report_where, objects_count, error }, rows_mode ('all' — отбор отчёта ≤ 20 позиций,
//   все изменения дня без порога; 'top' — крупнейшие по |Δ|), search (поиск, применённый сервером), matched (сколько строк дня прошло отбор и поиск).
// Новые ключи ответа модель пропускает молча (контракт расширяется только добавлением).
import { pmMenu_cmpApply } from './pmMenuGroups.js'

export const PMH_VERSION = 1
// порядок и ключи дорожек — как LANES бэка; подписи приходят с бэка на языке пользователя (здесь — запасные английские)
export const PMH_LANES = [
    { key: 'movements', title: 'Documents and movements' },
    { key: 'autoplan', title: 'Autoplan (proposals)' },
    { key: 'problems', title: 'Problems (uncovered)' },
    { key: 'stock', title: 'Stock' },
    { key: 'plan', title: 'Sales plans' },
]
const LANE_KEYS = new Set(PMH_LANES.map(l => l.key))

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0 }
const str = (v) => (v == null ? '' : String(v))
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v)

// дорожка меры — зеркало pm_history.lane_of бэка (строка без lane либо с неизвестным ключом)
export function pmHistory_laneOf(measure) {
    const m = str(measure)
    if (m.endsWith('_autoplan')) return 'autoplan'
    if (m.endsWith('_uncovered')) return 'problems'
    if (m === 'open_balance' || m === 'LS') return 'stock'
    if (m.startsWith('SP') || m.startsWith('M03')) return 'plan'
    return 'movements'
}

export function pmHistory_rowLane(c) {
    const k = str(c && c.lane)
    return LANE_KEYS.has(k) ? k : pmHistory_laneOf(c && c.measure)
}

// состояние ответа: 'loading' (данных ещё нет) | 'off' | 'no_archives' | 'error' | 'empty' (дней нет) | 'ok'
export function pmHistory_state(payload) {
    if (!isObj(payload)) return 'loading'
    const st = str(payload.state || 'ok')
    if (st === 'off') return 'off'
    if (st.startsWith('error')) return 'error'
    const days = Array.isArray(payload.days) ? payload.days : []
    if (!days.length) return st === 'no_archives' ? 'no_archives' : 'empty'
    if (st === 'no_archives' && days.length < 2) return 'no_archives'
    return 'ok'
}

export function pmHistory_errorText(payload) {
    const st = str(isObj(payload) ? payload.state : '')
    return st.startsWith('error') ? st.replace(/^error:\s*/, '') : ''
}

// дорожки ответа (подписи бэка) + известные дорожки, которых бэк не прислал; неизвестные ключи бэка — в конце, как пришли
export function pmHistory_lanes(payload) {
    const got = (isObj(payload) && Array.isArray(payload.lanes) ? payload.lanes : []).filter(l => isObj(l) && str(l.key))
    const out = []
    const seen = new Set()
    for (const L of PMH_LANES) {
        const b = got.find(x => str(x.key) === L.key)
        out.push({ key: L.key, title: str(b && b.title) || L.title })
        seen.add(L.key)
    }
    for (const b of got) if (!seen.has(str(b.key))) { out.push({ key: str(b.key), title: str(b.title) || str(b.key) }); seen.add(str(b.key)) }
    return out
}

// значение дорожки дня: { n, q } (старый формат — число ячеек без суммы)
export function pmHistory_laneValue(day, key) {
    const v = isObj(day) && isObj(day.lanes) ? day.lanes[key] : null
    if (v == null) return { n: 0, q: 0 }
    if (isObj(v)) return { n: num(v.n), q: Math.abs(num(v.q)) }
    return { n: num(v), q: 0 }
}

// 2026-10-28 → 28.10 (короткая подпись колонки); с годом — 28.10.2026
export function pmHistory_dayLabel(date, withYear = false) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str(date))
    if (!m) return str(date)
    return m[3] + '.' + m[2] + (withYear ? '.' + m[1] : '')
}

// число по-человечески: 16655532 → «16 655 532», дроби — до 2 знаков; null — «—»
export function pmHistory_fmt(v, signed = false) {
    if (v == null || v === '') return '—'
    const n = Number(v)
    if (!Number.isFinite(n)) return str(v)
    const r = Math.round(n * 100) / 100
    const s = Math.abs(r).toLocaleString('uk-UA', { maximumFractionDigits: 2 }).replace(/[\u00a0\u202f]/g, ' ').replace(',', '.')
    return (r < 0 ? '−' : (signed && r > 0 ? '+' : '')) + s
}

// компактно для подписи в пузыре: 8520 → 8.5k, 16655532 → 16.7M
export function pmHistory_short(v) {
    const n = Math.abs(num(v))
    if (n >= 1e6) return (Math.round(n / 1e5) / 10) + 'M'
    if (n >= 1e4) return Math.round(n / 1e3) + 'k'
    if (n >= 1e3) return (Math.round(n / 100) / 10) + 'k'
    return String(Math.round(n))
}

// РАСКЛАДКА ПУЗЫРЕЙ. o.mode: 'q' — размер по Σ|Δ| (по умолчанию), 'n' — по числу изменённых ячеек; o.selected — дата выбранного дня;
// o.lane — выбранная дорожка (''), o.min / o.max — диаметр пузыря, px. Размер — площадь пропорциональна значению (sqrt), масштаб — максимум по всей сетке;
// ненулевое изменение не меньше o.min. → { cols: [{ date, label, title, current, complete, selected, noDelta, changes, magnitude, error }],
// rows: [{ key, title, selected, total: { n, q }, cells: [{ date, n, q, size, selected, empty }] }], maxValue }
export function pmHistory_bubbles(payload, o = {}) {
    const mode = o.mode === 'n' ? 'n' : 'q'
    const min = o.min != null ? num(o.min) : 10
    const max = o.max != null ? num(o.max) : 34
    const days = isObj(payload) && Array.isArray(payload.days) ? payload.days.filter(isObj) : []
    const lanes = pmHistory_lanes(payload)
    const sel = str(o.selected != null ? o.selected : (isObj(payload) ? payload.selected : ''))
    const laneSel = str(o.lane)
    let maxValue = 0
    for (const d of days) for (const L of lanes) { const v = pmHistory_laneValue(d, L.key); maxValue = Math.max(maxValue, mode === 'n' ? v.n : v.q) }
    const sizeOf = (v) => {
        const x = mode === 'n' ? v.n : v.q
        if (!v.n && !x) return 0
        if (!maxValue || !x) return min
        return Math.round(min + (max - min) * Math.sqrt(Math.min(1, x / maxValue)))
    }
    const cols = days.map((d, i) => ({
        date: str(d.date), label: pmHistory_dayLabel(d.date), title: str(d.title), current: !!d.current, complete: d.complete !== false,
        selected: str(d.date) === sel, pending: pmHistory_dayPending(d), noDelta: i === 0 || d.changes == null, changes: d.changes == null ? null : num(d.changes),
        magnitude: d.magnitude == null ? null : num(d.magnitude), objects: d.objects == null ? null : num(d.objects), error: str(d.error),
    }))
    const rows = lanes.map(L => {
        const total = { n: 0, q: 0 }
        const cells = days.map(d => {
            const v = pmHistory_laneValue(d, L.key)
            total.n += v.n; total.q += v.q
            return { date: str(d.date), n: v.n, q: v.q, size: sizeOf(v), selected: str(d.date) === sel, empty: !v.n && !v.q }
        })
        return { key: L.key, title: L.title, selected: laneSel === L.key, total, cells }
    })
    return { cols, rows, maxValue, mode }
}

// выбранный день ответа (payload.selected, иначе последний)
export function pmHistory_selectedDay(payload) {
    const days = isObj(payload) && Array.isArray(payload.days) ? payload.days.filter(isObj) : []
    if (!days.length) return null
    const sel = str(payload.selected)
    return days.find(d => str(d.date) === sel) || days[days.length - 1]
}

export function pmHistory_prevDay(payload, date) {
    const days = isObj(payload) && Array.isArray(payload.days) ? payload.days.filter(isObj) : []
    const i = days.findIndex(d => str(d.date) === str(date))
    return i > 0 ? days[i - 1] : null
}

// ОТБОР СТРОК ТАБЛИЦЫ: строки дня (o.date; пусто — все), дорожки (o.lane), поиск (o.query: объект, узел, мера, месяц, страна — без учёта регистра,
// все слова; так — только со старым бэком без серверного поиска), месяцы (o.months — начало месяца строки) и меры (o.measures — имя меры).
// Порядок — как прислал бэк (по |Δ| убыв.). → [{ ...строка, lane, neg }]
export function pmHistory_rows(changes, o = {}) {
    const date = str(o.date), lane = str(o.lane)
    const words = str(o.query).toLowerCase().split(/\s+/).filter(Boolean)
    // IE 2026 10 28 / ODOO-5800 (заказ Игоря: вкладка History of changes в отчёте, вариант A): месяц / мера поля поиска отбираются на фронте, позиция / узел — на сервере (params.search)
    const months = (Array.isArray(o.months) ? o.months : []).map(str).filter(Boolean)
    const measures = new Set((Array.isArray(o.measures) ? o.measures : []).map(m => str(m).toLowerCase()).filter(Boolean))
    const out = []
    for (const c of (Array.isArray(changes) ? changes : [])) {
        if (!isObj(c)) continue
        if (date && str(c.date) && str(c.date) !== date) continue
        const ln = pmHistory_rowLane(c)
        if (lane && ln !== lane) continue
        if (months.length && !months.some(m => str(c.month).startsWith(m))) continue
        if (measures.size && !measures.has(str(c.measure).toLowerCase())) continue
        if (words.length) {
            const hay = [c.object_title, c.object, c.node, c.measure, c.month, c.country].map(str).join(' ').toLowerCase()
            if (!words.every(w => hay.includes(w))) continue
        }
        out.push({ ...c, lane: ln, neg: num(c.delta) < 0 })
    }
    return out
}

// сводка строк по дорожкам (для подписи «Autoplan 56 · Stock 148»): { key: count }
export function pmHistory_rowsByLane(rows) {
    const m = {}
    for (const r of (Array.isArray(rows) ? rows : [])) { const k = pmHistory_rowLane(r); m[k] = (m[k] || 0) + 1 }
    return m
}

// ЗАПРОС ДНЯ: params метода pm_history. Отбор отчёта бэк берёт сам (чипсы / WHERE виджета) — явные objects / countries не шлём.
// IE 2026 10 28 / ODOO-5800 (заказ Игоря: вкладка History of changes в отчёте, вариант A): search — подстрока названия позиции / узла,
// ищет сервер по всем изменённым ячейкам дня, не только по top.
const HISTORY_KEYS = ['version_slice', 'event_group', 'batch_group', 'depth']   // IE 2026 10 29 / ODOO-5800 (ie-dm7 #113853: «Історія змін» v2) · depth — IE 2026 10 29 / ODOO-5800 (ie-dm7 #114811: проба dev02 — глубина отбрасывалась из запроса)
// IE 2026 10 29 / ODOO-5800 (ie-dm7 #113853: «Історія змін» v2): history — настройки панели v2 { version_slice, event_group, batch_group } → params.history
// (только непустые ключи; без настроек ключа history нет — запрос v1 не меняется)
// IE 2026 10 29 / ODOO-5800 (ie-dm7 r62 #115629: D1 — клик по настройке истории ждал 3,0–3,6 с до отправки: запрос pm_history вёз весь view отчёта (у D&S Balance мегабайты; JSON.stringify — long task 1,1–1,2 с
// на клик, затем выгрузка), хотя бэк pm_history / pm_history_v2 / pm_history_v3 view не читает. slim — view не возим (widget_sendToBackend_exclude, как cell_docs_more / cellStory);
// выключатель fe.data.pmHistorySlim (pmHistory_slimOn; по умолчанию вкл, false — прежний запрос с view)
export function pmHistory_dayRequest(date, method = 'pm_history', binding = '_doc.pm_history', search = '', history = null, stage = '', slim = false) {
    const params = {}
    if (date) params.day = str(date)
    if (str(search).trim()) params.search = str(search).trim()
    if (isObj(history)) {   // IE 2026 10 29 / ODOO-5800 (ie-dm7 #113853: «Історія змін» v2)
        const h = {}
        for (const k of HISTORY_KEYS) if (str(history[k])) h[k] = str(history[k])
        // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114084/#114086: «Історія змін» v3 — лайт/глубокий, версии планов, правила, документы, горизонты)
        // pair — только пара из двух id; signals — только 1; horizons — только непустой список (все горизонты → null у компонента, ключ не шлётся)
        if (Array.isArray(history.pair) && history.pair.length === 2 && history.pair.every(x => str(x))) h.pair = history.pair.map(str)
        if (history.signals === 1 || history.signals === true || history.signals === '1') h.signals = 1
        if (Array.isArray(history.horizons)) { const hz = history.horizons.map(str).filter(Boolean); if (hz.length) h.horizons = hz }
        const stg = str(stage).toLowerCase()
        if (stg === 'fast' || stg === 'deep') h.stage = stg   // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114932: история fast → deep, стадии ответа): params.history.stage; без стадии (флаг выкл / старый BE) ключа нет
        if (Object.keys(h).length) params.history = h
    }
    const out = { command: 'executeWidgetMethod', method: method || 'pm_history', binding: binding || '_doc.pm_history',
        params, record_sendToBackend_exclude: ['_doc'] }
    if (slim === true) out.widget_sendToBackend_exclude = ['view']   // IE 2026 10 29 / ODOO-5800 (ie-dm7 r62 #115629: D1): без view отчёта
    return out
}
// IE 2026 10 29 / ODOO-5800 (ie-dm7 r62 #115629: D1): выключатель fe.data.pmHistorySlim (по умолчанию вкл). setting — (ключ, умолчание) → значение
export function pmHistory_slimOn(setting) {
    try { return typeof setting !== 'function' || setting('fe.data.pmHistorySlim', true) !== false } catch (e) { return true }
}
// IE 2026 10 29 / ODOO-5800 (ie-dm7 r62 #115629: D1): поля запроса вкладки без view (ленивая загрузка AttrTabPanel — тот же выключатель)
export function pmHistory_slimFields(setting) {
    return pmHistory_slimOn(setting) ? { widget_sendToBackend_exclude: ['view'] } : {}
}

// IE 2026 10 28 / ODOO-5800 (заказ Игоря: вкладка History of changes в отчёте, вариант A)
// ПОЛЕ ПОИСКА: слова-месяцы (2026, 2026-11) и имена мер (IN_autoplan; список — measures ответа и мер строк) отбираются на фронте,
// остальные слова одной строкой уходят бэку как params.search. → { server, months: [], measures: [] }
const MONTH_RE = /^(19|20)\d{2}(-\d{1,2})?$/
export function pmHistory_splitQuery(query, measures) {
    const known = new Map()
    for (const m of (Array.isArray(measures) ? measures : [])) if (str(m)) known.set(str(m).toLowerCase(), str(m))
    const out = { server: '', months: [], measures: [] }
    const rest = []
    for (const w of str(query).split(/\s+/).filter(Boolean)) {
        if (MONTH_RE.test(w)) out.months.push(w)
        else if (known.has(w.toLowerCase())) out.measures.push(known.get(w.toLowerCase()))
        else rest.push(w)
    }
    out.server = rest.join(' ')
    return out
}

// меры ответа: payload.measures + меры строк (порядок — как пришли)
export function pmHistory_measures(payload) {
    const P = isObj(payload) ? payload : {}
    const out = new Set((Array.isArray(P.measures) ? P.measures : []).map(str).filter(Boolean))
    for (const c of (Array.isArray(P.changes) ? P.changes : [])) if (isObj(c) && str(c.measure)) out.add(str(c.measure))
    return [...out]
}

// бэк ищет сам (ответ несёт ключ search) — иначе старый бэк: поиск по всем словам на фронте
export function pmHistory_serverSearch(payload) {
    return isObj(payload) && Object.prototype.hasOwnProperty.call(payload, 'search')
}

// ОТБОР ОТЧЁТА в истории: { fromReport, count (позиций отбора; null — без отбора), error (предикат не выполним над витриной — история без отбора) }
export function pmHistory_filterInfo(payload) {
    const f = isObj(payload) && isObj(payload.filter) ? payload.filter : {}
    const count = f.objects_count == null || f.objects_count === '' ? null : num(f.objects_count)
    return { fromReport: !!f.from_report, count, error: str(f.error) }
}

// ТАБЛИЦА ДНЯ по ответу: mode 'all' (все изменения отбора) | 'top' (крупнейшие по |Δ|), shown — строк пришло, total — сколько прошло отбор и поиск
// (matched; старый бэк — изменённых ячеек дня), search — поиск, применённый сервером, matched — null, если бэк его не прислал.
export function pmHistory_tableInfo(payload, day) {
    const P = isObj(payload) ? payload : {}
    const shown = Array.isArray(P.changes) ? P.changes.filter(isObj).length : 0
    const matched = P.matched == null || P.matched === '' ? null : num(P.matched)
    const total = matched != null ? matched : (isObj(day) && day.changes != null ? num(day.changes) : shown)
    return { mode: P.rows_mode === 'all' ? 'all' : 'top', shown, total, matched, search: str(P.search).trim(), truncated: !!P.truncated && total > shown }
}

// OPEN: отчёт на модели копии дня — params.process_model_override (как пункт архива в чипсе модели, AttrChips.selectPmChip);
// день «сегодня» (current) — снять подмену (отчёт на своей модели). meta — rec._meta: форма помечается изменённой. → { changed, process_model }
export function pmHistory_openApply(params, meta, day) {
    if (!isObj(params) || !isObj(meta) || !isObj(day)) return { changed: false, process_model: '' }
    const pm = day.current ? '' : str((isObj(day.open) && day.open.process_model) || day.pm_id)
    if (!day.current && !pm) return { changed: false, process_model: '' }
    if (str(params.process_model_override) === pm) return { changed: false, process_model: pm }
    params.process_model_override = pm
    meta.modified = true
    return { changed: true, process_model: pm }
}

// COMPARE: текущая модель против копии дня — тем же путём, что выбор модели в чипсе «Δ vs model» (pmMenuGroups.pmMenu_cmpApply:
// params.prev_process_model = копия, compare_with_prev включается; pick — прежние значения для «From settings» / ⊗ чипса).
// Отчёт открыт на архиве (process_model_override ≠ модели сравнения дня) — подмена снимается: сравнивается ТЕКУЩАЯ модель.
// → { changed, pick, compare_process_model }
export function pmHistory_compareApply(params, meta, pick, day) {
    const cmp = isObj(day) && isObj(day.compare) ? day.compare : null
    const id = str(cmp && cmp.compare_process_model)
    if (!isObj(params) || !isObj(meta) || !id) return { changed: false, pick: pick || null, compare_process_model: '' }
    let changed = false
    const own = str(cmp.process_model)
    const ovr = str(params.process_model_override)
    if (ovr && ovr !== own) { params.process_model_override = ''; meta.modified = true; changed = true }
    const r = pmMenu_cmpApply(params, meta, pick || null, id)
    return { changed: changed || r.changed, pick: r.pick, compare_process_model: id }
}

// IE 2026 10 29 / ODOO-5800 (ie-dm7 #114831: «Порівняти поточний з копією» — копия пары, запись «Моделі для порівняння», «Позначити зміни»; причина disabled)
// КОПИЯ ДЛЯ СРАВНЕНИЯ: day.compare.compare_process_model; у текущего дня (compare: None от BE) — копия выбранной пары (pair.from), когда пара
// заканчивается текущей моделью (kind_to current) и начинается копией (kind_from ≠ current). → { id, title } | null
export function pmHistory_compareTarget(day, pair) {
    const id = isObj(day) && isObj(day.compare) ? str(day.compare.compare_process_model) : ''
    if (id) return { id, title: '' }
    if (!isObj(day) || !day.current || !isObj(pair)) return null
    const from = str(pair.from)
    if (!from || str(pair.kind_to) !== 'current' || str(pair.kind_from) === 'current') return null
    return { id: from, title: str(pair.title_from) }
}
// СРАВНЕНИЕ С ЗАПИСЬЮ: «Модель для порівняння» отчёта (params.prev_process_model, поле вкладки «Порівняння») = копия; подмена ПМ отчёта снимается —
// сравнивается ТЕКУЩАЯ модель; compare_with_prev не трогаем (режим включает разовое select_compare «Позначити зміни»). → { changed }
export function pmHistory_compareSaveApply(params, meta, id) {
    const k = str(id)
    if (!isObj(params) || !isObj(meta) || !k) return { changed: false }
    let changed = false
    if (str(params.process_model_override)) { params.process_model_override = ''; changed = true }
    if (str(params.prev_process_model) !== k) { params.prev_process_model = k; changed = true }
    if (str(params.compare_model_mode) !== 'explicit') { params.compare_model_mode = 'explicit'; changed = true }   // IE 2026 10 29 / ODOO-5800 (ie-dm7 #115178: режим модели сравнения — compare_model_mode, compare_mode = rows|union не трогаем); прежде: compare_mode explicit вместе с prev_process_model — иначе умолчание yesterday перекроет выбор
    if (changed) meta.modified = true
    return { changed }
}
// IE 2026 10 29 / ODOO-5800 (webscm #115159: compare для read-only и при несохранённых правках — в памяти)
// РЕЖИМ «Порівняти поточний з копією»: 'save' — запись отчёта сохраняется (saveRecord), 'memory' — правка только в памяти + select_compare (как Open).
// В памяти — если роль «только чтение» (saveRecord закрыт readonly_deny), если в записи УЖЕ были несохранённые правки (meta.modified ДО нашей правки — saveRecord унёс бы
// их в общую запись) или выключен fe.ux.pmHistoryCompareSave. modifiedBefore надо снять ДО pmHistory_compareSaveApply (она сама ставит meta.modified).
// → { mode: 'save' | 'memory', reason: '' | 'flag' | 'readonly' | 'unsaved' }
export function pmHistory_compareSaveMode({ readonly, modifiedBefore, flagOn } = {}) {
    if (flagOn === false) return { mode: 'memory', reason: 'flag' }
    if (readonly === true) return { mode: 'memory', reason: 'readonly' }
    if (modifiedBefore === true) return { mode: 'memory', reason: 'unsaved' }
    return { mode: 'save', reason: '' }
}
// выключатель fe.ux.pmHistoryCompareSave (по умолчанию вкл; false — сравнение всегда в памяти). setting — (ключ, умолчание) → значение
export function pmHistory_compareSaveOn(setting) {
    try { return typeof setting !== 'function' || setting('fe.ux.pmHistoryCompareSave', true) !== false } catch (e) { return true }
}
// IE 2026 10 30 / ODOO-5800 (history actions in a widget copy)
// ДЕЙСТВИЕ В КОПИИ ВИДЖЕТА (Open / Compare с Shift/⌘/Ctrl или кнопкой ⧉): rec1 — запись КОПИИ (store.copyWidget_JSON), параметры правятся только в ней —
// open: pmHistory_openApply; compare: pmHistory_compareSaveApply (копия — новая несохранённая запись, поэтому сравнение всегда в памяти, без saveRecord).
// → действие загрузки копии { command, method, params?, record_sendToBackend_exclude } | null (нечего делать: нет записи / дня / копии для сравнения)
export function pmHistory_copyPrepare(rec1, { kind, day, cmpId } = {}) {   // IE 2026 10 30 / ODOO-5800 (history actions in a widget copy)
    const params = isObj(rec1) && isObj(rec1.doc) && isObj(rec1.doc.params) ? rec1.doc.params : null
    if (!params) return null
    if (!isObj(rec1._meta)) rec1._meta = {}
    if (kind === 'open') {
        if (!isObj(day)) return null
        const r = pmHistory_openApply(params, rec1._meta, day)
        if (!day.current && !r.process_model) return null
        return { command: 'executeWidgetMethod', method: 'select', record_sendToBackend_exclude: ['_doc'] }
    }
    if (kind === 'compare') {
        if (!str(cmpId)) return null
        pmHistory_compareSaveApply(params, rec1._meta, cmpId)
        return { command: 'executeWidgetMethod', method: 'select_compare', params: { compare_view: 'badges' }, record_sendToBackend_exclude: ['_doc'] }
    }
    return null
}
// кнопки «в копии виджета»: выключатель fe.ux.pmHistoryCopyActions (по умолчанию вкл); read-only — нет (клонирование — команда записи, utils/readonlyPolicy.js)
export function pmHistory_copyOn(setting, readonly) {   // IE 2026 10 30 / ODOO-5800 (history actions in a widget copy)
    if (readonly === true) return false
    try { return typeof setting !== 'function' || setting('fe.ux.pmHistoryCopyActions', true) !== false } catch (e) { return true }
}
// ПРИЧИНА DISABLED кнопок дня (состояние подсказки по Alt): kind open | compare → '' (доступна) | pending | noDay | alreadyCurrent | noCopy
export function pmHistory_dayBlock(kind, day, overrideNow, target, pending) {
    if (pending) return 'pending'
    if (!isObj(day)) return 'noDay'
    if (kind === 'open') return day.current ? (str(overrideNow) ? '' : 'alreadyCurrent') : (str((isObj(day.open) && day.open.process_model) || day.pm_id) ? '' : 'noCopy')
    return target && str(target.id) ? '' : 'noCopy'
}

// ===================== IE 2026 10 29 / ODOO-5800 (ie-dm7 #113853: «Історія змін» v2) =====================
// v2 ответа (флаг бэка not-pm_history_v2): versions [{ id, kind: archive|model|current, date, title, complete, version }] (по date), settings_effective
// { version_slice, event_group, event_group_effective, batch_group, batch_levels: [{ level, title, field }] }, what, delta_bars, calendar, summary.
// Признак v2 — ОБА ключа versions (массив) и settings_effective (объект); без них вкладка — ровно v1.
export const PMH_SLICES = ['day', 'month', 'year']
export const PMH_HUE = 230, PMH_SAT = 55, PMH_L_NEAR = 22, PMH_L_FAR = 100
export const PMH_FAR_STROKE = '#5b6170'

export function pmHistory_isV2(payload) {
    return isObj(payload) && Array.isArray(payload.versions) && isObj(payload.settings_effective)
}

// настройки ответа: незнакомые / пустые значения — умолчания (month / month / total); batch_levels — [{ level, title, field, value: 'level<N>' }]
export function pmHistory_settings(payload) {
    const S = isObj(payload) && isObj(payload.settings_effective) ? payload.settings_effective : {}
    const pick = (v, d) => (PMH_SLICES.includes(str(v)) ? str(v) : d)
    const levels = (Array.isArray(S.batch_levels) ? S.batch_levels : []).filter(isObj)
        .map((L, i) => { const level = num(L.level) || (i + 1); return { level, title: str(L.title), field: str(L.field), value: 'level' + level } })
    const bg = str(S.batch_group)
    const event_group = pick(S.event_group, 'month')
    return {
        version_slice: pick(S.version_slice, 'month'), event_group, event_group_effective: pick(S.event_group_effective, event_group),
        batch_group: bg === 'total' || levels.some(L => L.value === bg) ? bg : 'total', batch_levels: levels,
    }
}

// версии по дате; rank — удалённость от ближней (последней по дате): 0 — ближняя / текущая, n-1 — самая дальняя
export function pmHistory_versions(payload) {
    const vs = (isObj(payload) && Array.isArray(payload.versions) ? payload.versions : []).filter(v => isObj(v) && str(v.id))
        .map(v => ({ id: str(v.id), kind: str(v.kind), date: str(v.date), title: str(v.title), complete: v.complete !== false, version: str(v.version) }))
        .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
    const n = vs.length
    return vs.map((v, i) => ({ ...v, rank: n - 1 - i, current: v.kind === 'current' }))
}

// ЦВЕТ ВЕРСИИ по удалённости: один тон (синий), светлота линейно от PMH_L_NEAR (rank 0 — ближняя, темнее) до 100 % (rank n-1 — самая дальняя, белая);
// белая — с тёмно-серой рамкой (stroke). → { fill, stroke, l, white }
export function pmHistory_versionColor(rank, n) {
    const k = Math.max(1, (Number(n) || 1) - 1)
    const r = Math.min(k, Math.max(0, Number(rank) || 0))
    const l = (Number(n) || 1) <= 1 ? PMH_L_NEAR : Math.round((PMH_L_NEAR + (PMH_L_FAR - PMH_L_NEAR) * r / k) * 10) / 10
    const fill = `hsl(${PMH_HUE}, ${PMH_SAT}%, ${l}%)`
    const white = l >= 96
    return { fill, stroke: white ? PMH_FAR_STROKE : fill, l, white }
}

// бакет событий → дата оси: 'YYYY' → 'YYYY-01-01', 'YYYY-MM' → 'YYYY-MM-01', 'YYYY-MM-DD' — как есть; иначе ''
export function pmHistory_tDate(t) {
    const s = str(t).trim()
    if (/^\d{4}$/.test(s)) return s + '-01-01'
    if (/^\d{4}-\d{2}$/.test(s)) return s + '-01'
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
    return ''
}

// IE 2026 10 29 / ODOO-5800 (ie-dm7 #113976: правки по боту r55): подпись пакета — batch_title бэка; пусто — ключ; пустой ключ '' (уровень «Country» без страны) — emptyTitle (компонент передаёт t('(empty)'))
export function pmHistory_batchTitle(title, key, emptyTitle = '(empty)') {
    return str(title).trim() ? str(title) : (str(key) || str(emptyTitle))
}

// пакеты (batch) для селекта: what.batches, иначе ключи delta_bars. → [{ key, title }]
// // IE 2026 10 29 / ODOO-5800 (ie-dm7 #113976: правки по боту r55): пакет с пустым ключом '' (null) не отбрасывается — подпись emptyTitle
export function pmHistory_batches(payload, emptyTitle = '(empty)') {
    const P = isObj(payload) ? payload : {}
    const W = isObj(P.what) ? P.what : {}
    const got = (Array.isArray(W.batches) ? W.batches : []).filter(b => isObj(b) && b.key !== undefined).map(b => ({ key: str(b.key), title: pmHistory_batchTitle(b.title, b.key, emptyTitle) }))   // IE 2026 10 29 / ODOO-5800 (ie-dm7 #113976: правки по боту r55)
    if (got.length) return got
    return Object.keys(isObj(P.delta_bars) ? P.delta_bars : {}).map(k => ({ key: k, title: pmHistory_batchTitle('', k, emptyTitle) }))   // IE 2026 10 29 / ODOO-5800 (ie-dm7 #113976: правки по боту r55)
}

// IE 2026 10 29 / ODOO-5800 (ie-dm7 #113976: правки по боту r55)
// ЕСТЬ ЛИ ДАННЫЕ у пары пакет × вид: what.series[batch][kind] — хоть одна версия с точками, или delta_bars[batch][kind] — хоть один бакет со столбиками
export function pmHistory_seriesHasData(payload, batchKey, kind) {
    const P = isObj(payload) ? payload : {}
    const pick = (o) => { const B = isObj(o) ? o[str(batchKey)] : null; const K = isObj(B) ? B[str(kind)] : null; return isObj(K) ? K : null }
    const W = pick(isObj(P.what) ? P.what.series : null)
    if (W && Object.values(W).some(v => Array.isArray(v) && v.length)) return true
    const D = pick(P.delta_bars)
    return !!(D && Object.values(D).some(v => Array.isArray(v) && v.length))
}

// IE 2026 10 29 / ODOO-5800 (ie-dm7 #113976: правки по боту r55)
// УМОЛЧАНИЕ ПАКЕТА И ВИДА графиков What changed / Change by version: выбор пользователя (cur { batch, kind }) с данными — остаётся;
// вид без данных в выбранном пакете — первый вид пакета с данными; иначе первый пакет × первый вид с данными (пакеты — внешний цикл);
// данных нет нигде — прежний выбор, если он есть в списках, иначе первый пакет / первый вид. → { batch, kind }
export function pmHistory_seriesDefault(payload, batches, kinds, cur = null) {
    const bs = (Array.isArray(batches) ? batches : []).filter(isObj).map(b => str(b.key))
    const ks = (Array.isArray(kinds) ? kinds : []).filter(isObj).map(k => str(k.key)).filter(Boolean)
    const cb = isObj(cur) ? str(cur.batch) : '', ck = isObj(cur) ? str(cur.kind) : ''
    const bOk = isObj(cur) && bs.includes(cb), kOk = ks.includes(ck)
    if (bOk && kOk && pmHistory_seriesHasData(payload, cb, ck)) return { batch: cb, kind: ck }
    if (bOk && kOk) { const k = ks.find(x => pmHistory_seriesHasData(payload, cb, x)); if (k !== undefined) return { batch: cb, kind: k } }
    for (const b of bs) for (const k of ks) if (pmHistory_seriesHasData(payload, b, k)) return { batch: b, kind: k }
    return { batch: bOk ? cb : (bs.length ? bs[0] : ''), kind: kOk ? ck : (ks.length ? ks[0] : '') }
}

// IE 2026 10 29 / ODOO-5800 (ie-dm7 #113976: правки по боту r55)
// ПАНЕЛЬ НАСТРОЕК И ПОДВКЛАДКИ v2 видны всегда, когда ответ v2 и в нём ≥ 1 версия — в т. ч. в пустом состоянии (срез «Year» даёт одну точку →
// state 'no_archives'): иначе вернуться к «Day» без перезагрузки нельзя. Пустое состояние — тогда внутри подвкладки. v1 / загрузка / off — false.
export function pmHistory_v2Panel(payload) {
    if (!pmHistory_isV2(payload)) return false
    const st = pmHistory_state(payload)
    if (st === 'ok') return true
    if (st === 'loading' || st === 'off') return false
    return pmHistory_versions(payload).length >= 1
}

// IE 2026 10 29 / ODOO-5800 (ie-dm7 #113976: правки по боту r55)
// |Δ| строки calendar.top[]: abs бэка (|Δ|, по нему сортировка), нет — |delta| (delta — net со знаком)
export function pmHistory_topAbs(m) {
    if (!isObj(m)) return 0
    const a = m.abs == null || m.abs === '' ? NaN : Number(m.abs)
    return Number.isFinite(a) ? Math.abs(a) : Math.abs(num(m.delta))
}

// виды (kind) what: строки или { key, title } → [{ key, title }] (title — бэка, пусто — подписывает компонент)
export function pmHistory_kinds(payload) {
    const W = isObj(payload) && isObj(payload.what) ? payload.what : {}
    const out = []
    for (const k of (Array.isArray(W.kinds) ? W.kinds : [])) {
        const key = isObj(k) ? str(k.key) : str(k)
        if (key && !out.some(x => x.key === key)) out.push({ key, title: isObj(k) ? str(k.title) : '' })
    }
    if (out.length) return out
    return PMH_LANES.map(L => ({ key: L.key, title: '' }))
}

// УРОВНИ КАЛЕНДАРЯ: 4 корзины по квантилям abs_sum среди точек calendar[] (ранг (i+1)/n, равные — один уровень); abs_sum 0 — уровень 0. → { date: 0..3 }
export function pmHistory_calendarLevels(calendar) {
    const pts = (Array.isArray(calendar) ? calendar : []).filter(p => isObj(p) && str(p.date))
    const vals = pts.map(p => Math.abs(num(p.abs_sum))).sort((a, b) => a - b)
    const n = vals.length
    const out = {}
    for (const p of pts) {
        const v = Math.abs(num(p.abs_sum))
        if (!v) { out[str(p.date)] = 0; continue }
        const i = vals.lastIndexOf(v)
        out[str(p.date)] = Math.min(3, Math.max(0, Math.ceil(4 * (i + 1) / n) - 1))
    }
    return out
}

// МЕСЯЦЫ КАЛЕНДАРЯ: месяцы с точками calendar[] от нового к старому; сетка 7 колонок Пн..Вс — отступ первого дня (weekday + 6) % 7.
// → [{ ym, year, month, offset, days: [{ date, n, point, level }] }]
export function pmHistory_calendarMonths(calendar) {
    const pts = (Array.isArray(calendar) ? calendar : []).filter(p => isObj(p) && /^\d{4}-\d{2}-\d{2}/.test(str(p.date)))
    const levels = pmHistory_calendarLevels(pts)
    const byDate = {}
    for (const p of pts) { const d = str(p.date).slice(0, 10); if (!byDate[d] || num(p.abs_sum) > num(byDate[d].abs_sum)) byDate[d] = p }
    const yms = [...new Set(Object.keys(byDate).map(d => d.slice(0, 7)))].sort().reverse()
    return yms.map(ym => {
        const year = +ym.slice(0, 4), month = +ym.slice(5, 7)
        const offset = (new Date(Date.UTC(year, month - 1, 1)).getUTCDay() + 6) % 7
        const count = new Date(Date.UTC(year, month, 0)).getUTCDate()
        const days = []
        for (let d = 1; d <= count; d++) {
            const date = ym + '-' + String(d).padStart(2, '0')
            const point = byDate[date] || null
            days.push({ date, n: d, point, level: point ? (levels[str(point.date)] || 0) : 0 })
        }
        return { ym, year, month, offset, days }
    })
}

// точка календаря дня (наибольшая по abs_sum, если их несколько)
export function pmHistory_calendarPoint(calendar, date) {
    let best = null
    for (const p of (Array.isArray(calendar) ? calendar : [])) if (isObj(p) && str(p.date).slice(0, 10) === str(date) && (!best || num(p.abs_sum) > num(best.abs_sum))) best = p
    return best
}

// СВОДКА: строки summary.by_level[группировка] (нет уровня — null); by_slice — [{ key: day|month|year, versions, cells, abs_sum }] (только пришедшие)
export function pmHistory_summaryRows(payload, group) {
    const S = isObj(payload) && isObj(payload.summary) ? payload.summary : {}
    const L = isObj(S.by_level) ? S.by_level[str(group) || 'total'] : null
    return Array.isArray(L) ? L.filter(isObj) : null
}
export function pmHistory_summarySlices(payload) {
    const S = isObj(payload) && isObj(payload.summary) && isObj(payload.summary.by_slice) ? payload.summary.by_slice : {}
    return PMH_SLICES.filter(k => isObj(S[k])).map(k => ({ key: k, versions: S[k].versions, cells: S[k].cells, abs_sum: S[k].abs_sum }))
}

// IE 2026 10 29 / ODOO-5800 (ie-dm7 #114070: «Історія змін» v2, итерация 2 Igor)
// БЕЙДЖИ кнопок (контракт badges BE → FE, спека ie-dm7 work-pm-history-v2.md «Итерация 2»): tabs.{when,what,delta,calendar,summary},
// kinds.{movements,autoplan,problems,stock,plan} — число позиций (distinct object) с изменениями; version_slice.* — точек ряда; event_group.* — бакетов;
// batch_group.{total,level<N>} — пакетов уровня. Нет ключа badges / группы / значения, 0, отрицательное, не число → 0 (бейдж не рисуется).
export const PMH_BADGE_GROUPS = ['tabs', 'kinds', 'version_slice', 'event_group', 'batch_group', 'horizons', 'depth']   // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114084/#114086: «Історія змін» v3 — лайт/глубокий, версии планов, правила, документы, горизонты) // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114811: глубина истории, lite_columns, by_horizon): + depth
export function pmHistory_badge(payload, group, key) {
    const B = isObj(payload) && isObj(payload.badges) ? payload.badges : null
    const G = B && isObj(B[str(group)]) ? B[str(group)] : null
    if (!G) return 0
    const v = G[str(key)]
    const n = v == null || v === '' || typeof v === 'boolean' ? NaN : Number(v)
    return Number.isFinite(n) && n > 0 ? Math.round(n) : 0
}
// текст бейджа: до 999 — число, дальше — коротко (1.2k, 35k, 1.4M, 124M) — не длиннее 4 знаков до миллиарда
export function pmHistory_badgeText(n) {
    const v = Number(n)
    if (!(Number.isFinite(v) && v > 0)) return ''
    if (v < 1e3) return String(Math.round(v))
    if (v < 1e4) return (Math.round(v / 100) / 10) + 'k'
    if (v < 999500) return Math.round(v / 1e3) + 'k'
    if (v < 1e7) return (Math.round(v / 1e5) / 10) + 'M'
    return Math.round(v / 1e6) + 'M'
}
// карта бейджей { group: { key: 'текст' } } — только ненулевые; шаблон: v-if="bd.tabs.when"
export function pmHistory_badgeMap(payload) {
    const out = {}
    for (const g of PMH_BADGE_GROUPS) {
        out[g] = {}
        const G = isObj(payload) && isObj(payload.badges) && isObj(payload.badges[g]) ? payload.badges[g] : {}
        for (const k of Object.keys(G)) { const t = pmHistory_badgeText(pmHistory_badge(payload, g, k)); if (t) out[g][k] = t }
    }
    return out
}

// IE 2026 10 29 / ODOO-5800 (ie-dm7 #114084/#114086: «Історія змін» v3 — лайт/глубокий, версии планов, правила, документы, горизонты)
// v3 (спека ie-dm7 work-pm-history-v3-analysis.md §4, допущения #114089): признак — ключ lite (массив) в ответе. Лайт — одна строка на позицию по паре
// версий (pair {from, to}) с причинами why[]; глубокий — подвкладки v2 + what.plan_versions / rules / docs. Горизонты (work-pm-history-v2.md «Горизонты»):
// horizons[] ответа, выбор — settings_effective.horizons, запрос params.history.horizons (все выбраны → ключ не шлётся). Без ключей — поведение v2.
export const PMH_WHY_KINDS = ['fact', 'doc', 'plan_version', 'rule', 'autoplan']
const LITE_DELTAS = ['delta_plan', 'delta_in', 'delta_uncovered', 'delta_stock']
const LITE_EXTRA = ['cells', 'delta_out', 'delta_autoplan']   // // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114113: добавки BE v3 — errors_v3, lite_total, титулы, batch_supported)
const numOrNull = (v) => { if (v == null || v === '' || typeof v === 'boolean') return null; const n = Number(v); return Number.isFinite(n) ? n : null }

export function pmHistory_isV3(payload) {
    return isObj(payload) && Array.isArray(payload.lite)
}

// ПАРЫ ВЕРСИЙ для селектора лайта: соседние версии по дате (pmHistory_versions) → [{ key: 'from>to', from, to, title_from, title_to }] — от старой к новой
export function pmHistory_pairs(versions) {
    const vs = (Array.isArray(versions) ? versions : []).filter(v => isObj(v) && str(v.id)).slice()
        .sort((a, b) => (str(a.date) < str(b.date) ? -1 : str(a.date) > str(b.date) ? 1 : 0))
    const vt = (v) => str(v.title) || pmHistory_dayLabel(v.date, true) || str(v.id)
    const out = []
    for (let i = 1; i < vs.length; i++) out.push({ key: str(vs[i - 1].id) + '>' + str(vs[i].id), from: str(vs[i - 1].id), to: str(vs[i].id), title_from: vt(vs[i - 1]), title_to: vt(vs[i]),
        kind_from: str(vs[i - 1].kind), kind_to: str(vs[i].kind) })   // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114113: добавки BE v3 — errors_v3, lite_total, титулы, batch_supported): kind версии (archive / model / current) — пометка у заголовка пары
    return out
}
// IE 2026 10 29 / ODOO-5800 (ie-dm7 #114084, проба 6: подпись пары обрезалась)
// КОРОТКАЯ ПОДПИСЬ ПАРЫ для <option>: архивная копия — дата (28.10.2026), model / current — title; у current — суффикс (curWord, через t()).
// versionsById — { id: { date, title, kind } } (из pmHistory_versions); нет версии / даты — title пары. «26.10.2026 → PM-2026 10 01 (current)»
export function pmHistory_pairLabel(pair, versionsById, curWord = 'current') {
    if (!isObj(pair)) return ''
    const V = isObj(versionsById) ? versionsById : {}
    const end = (id, title, kind) => {
        const v = isObj(V[id]) ? V[id] : null
        const k = str(kind) || (v ? str(v.kind) : '')
        const base = k === 'archive' && v && str(v.date) ? pmHistory_dayLabel(v.date, true) : (str(title) || (v ? str(v.title) : '') || str(id))
        return k === 'current' ? base + ' (' + curWord + ')' : base
    }
    return end(pair.from, pair.title_from, pair.kind_from) + ' → ' + end(pair.to, pair.title_to, pair.kind_to)
}
// пара ответа: pair {from, to} из пар versions; нет / не из списка — последняя пара (умолчание BE). → пара или null
export function pmHistory_pairCurrent(payload) {
    const pairs = pmHistory_pairs(isObj(payload) ? payload.versions : null)
    const P = isObj(payload) && isObj(payload.pair) ? payload.pair : null
    const hit = P ? pairs.find(x => x.from === str(P.from) && x.to === str(P.to)) : null
    const cur = hit || (pairs.length ? pairs[pairs.length - 1] : null)
    // // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114113: добавки BE v3 — errors_v3, lite_total, титулы, batch_supported): pair ответа несёт from_kind / to_kind — они точнее списка versions
    if (cur && hit && P) return { ...cur, kind_from: str(P.from_kind) || cur.kind_from, kind_to: str(P.to_kind) || cur.kind_to }
    return cur
}

// IE 2026 10 29 / ODOO-5800 (ie-dm7 #114811: глубина истории, lite_columns, by_horizon)
// КОЛОНКИ ЛАЙТА: lite_columns BE [{ key, class, title, measures, accent }] → [{ key, cls, title, measures, accent, fallback }] в порядке BE (без key — пропуск, дубли — первый);
// нет ключа / пусто — прежние шесть колонок (fallback: true, title '' — подпись берёт компонент через t())
export const PMH_LITE_COLS_DEFAULT = ['delta_plan', 'delta_in', 'delta_out', 'delta_autoplan', 'delta_uncovered', 'delta_stock']
export function pmHistory_liteColumns(payload) {
    const out = []
    for (const c of (isObj(payload) && Array.isArray(payload.lite_columns) ? payload.lite_columns : [])) {
        if (!isObj(c) || !str(c.key) || out.some(x => x.key === str(c.key))) continue
        out.push({ key: str(c.key), cls: str(c.class), title: str(c.title) || str(c.key), measures: (Array.isArray(c.measures) ? c.measures : []).map(str).filter(Boolean), accent: c.accent === true, fallback: false })
    }
    return out.length ? out : PMH_LITE_COLS_DEFAULT.map(key => ({ key, cls: '', title: '', measures: [], accent: false, fallback: true }))
}

// ГЛУБИНА ИСТОРИИ (work-pm-history-v2.md «Глибина»): settings_effective.depth ∈ PMH_DEPTHS; badges.depth[k] — точек версий в окне глубины.
// → { available (BE знает глубину; старый ответ — false, группа не рисуется), value, options: [{ value, badge (число | 0) }], widened (нет пары в окне → две последние точки) }
export const PMH_DEPTHS = ['week', 'month', 'quarter', 'year', 'all']
export function pmHistory_depth(payload) {
    const S = isObj(payload) && isObj(payload.settings_effective) ? payload.settings_effective : {}
    const value = PMH_DEPTHS.includes(str(S.depth)) ? str(S.depth) : ''
    return {
        available: !!value, value,
        options: PMH_DEPTHS.map(k => ({ value: k, badge: pmHistory_badge(payload, 'depth', k) })),
        widened: ((isObj(payload) && payload.depth_widened === true) || S.depth_widened === true),
    }
}

// СТРОКИ ЛАЙТА: lite[] → [{ object, title, delta_plan, delta_in, delta_uncovered, delta_stock (число | null), amax, why: [{ kind, n, note }] }];
// порядок — по max |Δ| убыв. (сортировка устойчивая: при равных — порядок BE); o.sort === 'be' — порядок BE как есть
export function pmHistory_liteRows(payload, o = {}) {
    const L = isObj(payload) && Array.isArray(payload.lite) ? payload.lite : []
    const cols = pmHistory_liteColumns(payload)   // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114811: глубина истории, lite_columns, by_horizon)
    const accent = cols.find(c => c.accent)   // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114811: глубина истории, lite_columns, by_horizon)
    const rows = L.filter(isObj).map((r, i) => {
        const out = { object: str(r.object), title: str(r.title) || str(r.object), i }
        for (const k of LITE_DELTAS) out[k] = numOrNull(r[k])
        for (const k of LITE_EXTRA) out[k] = numOrNull(r[k])   // // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114113: добавки BE v3 — errors_v3, lite_total, титулы, batch_supported): cells, delta_out, delta_autoplan
        for (const c of cols) if (!(c.key in out)) out[c.key] = numOrNull(r[c.key])   // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114811: глубина истории, lite_columns, by_horizon): delta_accent и колонки классов мер
        out.amax = Math.max(0, ...LITE_DELTAS.map(k => Math.abs(out[k] || 0)))
        if (accent) out.amax = Math.abs(out[accent.key] || 0)   // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114811: глубина истории, lite_columns, by_horizon): с акцентом строки — по |Δ акцента|, как у BE
        out.why = (Array.isArray(r.why) ? r.why : []).filter(w => isObj(w) && str(w.kind)).map(w => ({ kind: str(w.kind), n: numOrNull(w.n), note: str(w.note) }))
        return out
    })
    if (o.sort !== 'be') rows.sort((a, b) => (b.amax - a.amax) || (a.i - b.i))
    return rows
}
// // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114113: добавки BE v3 — errors_v3, lite_total, титулы, batch_supported)
// всего позиций пары на сервере (lite_total, int) или null; «{n} of {total} items» — при lite_limited либо когда lite_total больше показанных строк
export function pmHistory_liteTotal(payload) {
    const n = isObj(payload) ? numOrNull(payload.lite_total) : null
    return n != null && n >= 0 ? Math.floor(n) : null
}
export function pmHistory_liteOfTotal(payload) {
    const total = pmHistory_liteTotal(payload)
    const shown = isObj(payload) && Array.isArray(payload.lite) ? payload.lite.filter(isObj).length : 0
    return total != null && ((isObj(payload) && payload.lite_limited === true) || total > shown) ? { n: shown, total } : null
}
// ОШИБКА БЛОКА (errors_v3: [{ block, error }], block = lite | plan_versions | rules | docs): текст ошибки блока; нет записи — ''; пустой текст при записи — 'error'
export function pmHistory_blockError(payload, block) {
    const E = isObj(payload) && Array.isArray(payload.errors_v3) ? payload.errors_v3 : []
    const hit = E.find(e => isObj(e) && str(e.block) === str(block))
    return hit ? (str(hit.error) || 'error') : ''
}
// подпись «показано n из total / первые n строк» для rules / docs: total больше строк → { n, total }; иначе limited → { n }; иначе null
export function pmHistory_shown(rowsLen, total, limited) {
    const n = Number(rowsLen) || 0, t = numOrNull(total)
    if (t != null && t > n) return { n, total: t }
    return limited ? { n } : null
}

// СВОДКА ЛАЙТА (данные строки «{n} items · fact … · recalculation only …», без t): lite_summary BE; нет — счёт по lite[] (позиций с причиной вида; без причин — recalc_only)
export function pmHistory_liteSummary(payload) {
    const S = isObj(payload) && isObj(payload.lite_summary) ? payload.lite_summary : null
    const keys = ['objects'].concat(PMH_WHY_KINDS, ['recalc_only'])
    const out = {}
    if (S) { for (const k of keys) out[k] = num(S[k]); return out }
    const rows = pmHistory_liteRows(payload, { sort: 'be' })
    out.objects = rows.length
    for (const k of PMH_WHY_KINDS) out[k] = rows.filter(r => r.why.some(w => w.kind === k)).length
    out.recalc_only = rows.filter(r => !r.why.length).length
    return out
}

// ГОРИЗОНТЫ ответа → [{ key, title, from, to, source }] (без ключа — пропуск, дубли — первый)
export function pmHistory_horizons(payload) {
    const out = []
    for (const h of (isObj(payload) && Array.isArray(payload.horizons) ? payload.horizons : [])) {
        if (!isObj(h) || !str(h.key) || out.some(x => x.key === str(h.key))) continue
        out.push({ key: str(h.key), title: str(h.title) || str(h.key), from: str(h.from), to: str(h.to), source: str(h.source) })
    }
    return out
}
// подпись диапазона горизонта: «from — to»; открытый край — «≤ to» / «≥ from»; оба пусто — ''
export function pmHistory_horizonRange(h) {
    const a = isObj(h) ? str(h.from) : '', b = isObj(h) ? str(h.to) : ''
    if (a && b) return a === b ? a : a + ' — ' + b
    return b ? '≤ ' + b : (a ? '≥ ' + a : '')
}
// выбранные горизонты: settings_effective.horizons (только известные ключи, в порядке horizons); пусто / нет — все
export function pmHistory_horizonsSelected(payload) {
    const all = pmHistory_horizons(payload).map(h => h.key)
    const S = isObj(payload) && isObj(payload.settings_effective) ? payload.settings_effective.horizons : null
    const sel = Array.isArray(S) ? all.filter(k => S.map(str).includes(k)) : []
    return sel.length ? sel : all
}
// горизонты запроса: неполный непустой выбор → ключи в порядке all; все / пусто → null (ключ не шлётся)
export function pmHistory_horizonsRequest(selected, all) {
    const A = (Array.isArray(all) ? all : []).map(str).filter(Boolean)
    const S = new Set((Array.isArray(selected) ? selected : []).map(str))
    const sel = A.filter(k => S.has(k))
    return sel.length && sel.length < A.length ? sel : null
}
// нажатие кнопки горизонта: отжать / нажать; последний выбранный не отжимается (тот же массив). → новый выбор в порядке all
export function pmHistory_horizonToggle(selected, key, all) {
    const A = (Array.isArray(all) ? all : []).map(str).filter(Boolean)
    const cur = Array.isArray(selected) ? selected.map(str) : []
    const k = str(key)
    if (!A.includes(k)) return cur
    if (cur.includes(k)) return cur.length <= 1 ? cur : A.filter(x => x !== k && cur.includes(x))
    return A.filter(x => x === k || cur.includes(x))
}
// IE 2026 10 29 / ODOO-5800 (ie-dm7 #114825: мультигруппа горизонтов — клик = только этот, Shift/⌘/Ctrl = добавить/снять)
// МУЛЬТИГРУППА строки настроек (горизонты и будущие): обычный клик — только этот ключ (клик по единственному выбранному — снова все);
// additive (Shift / ⌘ / Ctrl) — добавить/снять ключ в наборе, последний не снимается (pmHistory_horizonToggle)
export function pmHistory_multiPick(selected, key, all, additive) {
    const A = (Array.isArray(all) ? all : []).map(str).filter(Boolean)
    const cur = Array.isArray(selected) ? selected.map(str) : []
    const k = str(key)
    if (!A.includes(k)) return cur
    if (additive) return pmHistory_horizonToggle(cur, k, A)
    return cur.length === 1 && cur[0] === k ? A.slice() : [k]
}
export function pmHistory_multiAdditive(ev) { return !!(ev && (ev.shiftKey || ev.metaKey || ev.ctrlKey)) }
// СВОДКА ПО ГОРИЗОНТАМ: summary.by_horizon → [{ key, title, from, to, rows }] — в порядке horizons, ключи вне horizons — в конце; нет by_horizon — []
export function pmHistory_summaryByHorizon(payload) {
    const B = isObj(payload) && isObj(payload.summary) && isObj(payload.summary.by_horizon) ? payload.summary.by_horizon : null
    if (!B) return []
    const hs = pmHistory_horizons(payload)
    const keys = hs.map(h => h.key).filter(k => Array.isArray(B[k])).concat(Object.keys(B).filter(k => Array.isArray(B[k]) && !hs.some(h => h.key === k)))
    return keys.map(k => { const h = hs.find(x => x.key === k) || { key: k, title: k, from: '', to: '' }; return { key: k, title: h.title, from: h.from, to: h.to, rows: B[k].filter(isObj) } })
}

// ВЕРСИИ ПЛАНОВ M03 (what.plan_versions): нет ключа — null; versions по date_from; delta[версия] {cells, objects, abs, net}; пакеты — ключи series
export function pmHistory_planVersions(payload) {
    const W = isObj(payload) && isObj(payload.what) ? payload.what : null
    const P = W && isObj(W.plan_versions) ? W.plan_versions : null
    if (!P) return null
    const D = isObj(P.delta) ? P.delta : {}
    const versions = (Array.isArray(P.versions) ? P.versions : []).filter(v => isObj(v) && str(v.id))
        .map(v => ({ id: str(v.id), title: str(v.title) || str(v.date_from) || str(v.id), date_from: str(v.date_from), date_to: str(v.date_to), docs: numOrNull(v.docs) }))   // // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114113: добавки BE v3 — errors_v3, lite_total, титулы, batch_supported): date_to, docs
        .sort((a, b) => (a.date_from < b.date_from ? -1 : a.date_from > b.date_from ? 1 : 0))
    const delta = versions.map(v => { const d = isObj(D[v.id]) ? D[v.id] : null; return { ...v, has: !!d, cells: d ? numOrNull(d.cells) : null, objects: d ? numOrNull(d.objects) : null, abs: d ? numOrNull(d.abs) : null, net: d ? numOrNull(d.net) : null } })
    // // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114113: добавки BE v3 — errors_v3, lite_total, титулы, batch_supported): batch_supported === false (уровень пакета не object / country) — BE отдаёт только total
    const supported = P.batch_supported !== false
    const series = isObj(P.series) ? P.series : {}
    const batches = Object.keys(series).filter(k => supported || k === 'total')
    return { versions, delta, batches, series, batch_field: str(P.batch_field), batch_supported: supported, has_to: versions.some(v => v.date_to), has_docs: versions.some(v => v.docs != null) }
}

// ПРАВИЛА (rules): нет ключа — null. segments — by_segment строками; поля счётчиком «f n» по убыванию; rows; limited
export function pmHistory_rules(payload) {
    const R = isObj(payload) && isObj(payload.rules) ? payload.rules : null
    if (!R) return null
    const BS = isObj(R.by_segment) ? R.by_segment : {}
    const segments = Object.keys(BS).filter(k => isObj(BS[k])).map(k => {
        const s = BS[k], F = isObj(s.fields) ? s.fields : {}
        const fields = Object.keys(F).map(f => ({ f, n: num(F[f]) })).sort((a, b) => b.n - a.n)
        return { segment: k, added: num(s.added), changed: num(s.changed), archived: num(s.archived), objects: num(s.objects), fields }
    })
    const rows = (Array.isArray(R.rows) ? R.rows : []).filter(isObj).map(r => ({
        segment: str(r.segment), id: str(r.id), object: str(r.object_title) || str(r.object), signal: r.signal == null ? '' : (isObj(r.signal) ? JSON.stringify(r.signal) : str(r.signal)), subject_from: str(r.subject_from), subject_to: str(r.subject_to), op: str(r.op).toUpperCase(),
        fields: (Array.isArray(r.fields) ? r.fields : []).filter(isObj).map(f => ({ f: str(f.f), from: str(f.from), to: str(f.to) })), by: str(r.by), at: str(r.at),
    }))
    // // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114113: добавки BE v3 — errors_v3, lite_total, титулы, batch_supported): limited / total / window / signals (bool — состояние тумблера на сервере), строки — object_title (иначе object), signal
    // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114366, BE #600: limited — только недочитанное окно (chunks_done < chunks), rows_limited — усечение строк (500 / 3000 при signals); «Read more» — по limited, «Shown n of total» — по rows_limited)
    return { segments, rows, limited: R.limited === true, rows_limited: R.rows_limited === true, chunk_mode: str(R.chunk_mode), total: numOrNull(R.total), window: R.window == null ? null : R.window, signals: typeof R.signals === 'boolean' ? R.signals : null,
        has_signal: rows.some(r => r.signal), pairs: Array.isArray(R.pairs) ? R.pairs : [] }
}
// IE 2026 10 29 / ODOO-5800 (ie-dm7 #114153: правила по суткам — частично до даты, «дочитать»)
// окно чтения правил: rules.window_done {from,to} — аудит досчитан до даты to; partial — только при limited и непустом to (повторный запрос с теми же params дочитывает из кэша)
export function pmHistory_rulesWindow(payload) {
    const R = isObj(payload) && isObj(payload.rules) ? payload.rules : null
    const W = R && isObj(R.window_done) ? R.window_done : null
    const to = W ? str(W.to) : '', from = W ? str(W.from) : ''
    // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114303, проба 10: limited стоит и при лимите строк (500 из 11 904) — тогда «дочитать» нечего; окно не дочитано только при chunks_done < chunks (BE #594); нет счётчиков — как раньше, по limited)
    const chunks = R ? numOrNull(R.chunks) : null, chunksDone = R ? numOrNull(R.chunks_done) : null
    const unread = chunks != null && chunksDone != null ? chunksDone < chunks : (R && R.limited === true)
    return { to, from, partial: !!(R && unread && to), chunks, chunks_done: chunksDone }
}
// «subject_from → subject_to» / «from → to»: одинаковые или одно пустое — одно значение
export function pmHistory_arrow(from, to) {
    const a = str(from), b = str(to)
    if (!a || !b || a === b) return a || b
    return a + ' → ' + b
}

// ДОКУМЕНТЫ (docs): нет ключа — null. groups — by_group строками; statuses «from→to» → [{ key, from, to, n }] по убыванию n; rows; limited
export function pmHistory_docs(payload) {
    const D = isObj(payload) && isObj(payload.docs) ? payload.docs : null
    if (!D) return null
    const BG = isObj(D.by_group) ? D.by_group : {}
    const groups = Object.keys(BG).filter(k => isObj(BG[k])).map(k => {
        const g = BG[k]
        return { group: k, added: num(g.added), removed: num(g.removed), status_chg: num(g.status_chg), qty_chg: num(g.qty_chg), date_chg: num(g.date_chg), objects: num(g.objects) }
    })
    const ST = isObj(D.statuses) ? D.statuses : {}
    const statuses = Object.keys(ST).map(k => { const [from, to] = k.split(/\s*(?:→|->)\s*/); return { key: k, from: str(from), to: str(to), n: num(ST[k]) } }).filter(s => s.n).sort((a, b) => b.n - a.n)
    // // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114113: добавки BE v3 — errors_v3, lite_total, титулы, batch_supported): строки — титулы (object_title / subject_title, иначе id), op, source; limited / total
    const rows = (Array.isArray(D.rows) ? D.rows : []).filter(isObj).map(r => ({ ...r, object: str(r.object_title) || str(r.object), subject: str(r.subject_title) || str(r.subject), op: str(r.op).toUpperCase(), source: str(r.source) }))
    return { groups, statuses, rows, limited: D.limited === true || D.rows_limited === true, total: numOrNull(D.total), has_op: rows.some(r => r.op), has_source: rows.some(r => r.source) }
}

// IE 2026 10 29 / ODOO-5800 (ie-dm7 #114232: partial/done — partial_blocks, done, badges_partial, общий «Read more»)
// ЧАСТИЧНЫЙ ОТВЕТ (бюджет времени BE кончился): partial, partial_blocks — какие блоки не дочитаны, done — счётчики дочитанного, badges_partial — бейджи по неполным данным.
// Повтор того же запроса дочитывает из серверного кэша (на бою кэш у каждого воркера свой — иногда нужно 2–3 нажатия).
export const PMH_PARTIAL_BLOCKS = ['pairs', 'changes', 'series', 'lite', 'plan_versions', 'fact', 'docs', 'rules', 'slices']
const PMH_PARTIAL_TITLES = { pairs: 'Pairs', changes: 'Changes', series: 'Series', lite: 'Lite', plan_versions: 'Plan versions', fact: 'Fact', docs: 'Documents', rules: 'Rules', slices: 'Slices' }
const doneOf = (o, d, t) => (isObj(o) ? { done: num(o[d] != null ? o[d] : o.done), total: num(o[t] != null ? o[t] : o.total) } : null)
// → { partial, blocks (известные ключи, без дублей, в порядке ответа), done: { pairs: {done,total}|null, plan_versions: {done,total}|null }, badges }
export function pmHistory_partial(payload) {
    const P = isObj(payload) ? payload : {}
    const blocks = []
    for (const b of (Array.isArray(P.partial_blocks) ? P.partial_blocks : [])) { const k = str(b); if (PMH_PARTIAL_BLOCKS.includes(k) && !blocks.includes(k)) blocks.push(k) }
    const D = isObj(P.done) ? P.done : {}
    return {
        partial: P.partial === true || blocks.length > 0,
        blocks,
        done: { pairs: doneOf(D.pairs, 'done', 'total'), plan_versions: doneOf(D.plan_versions, 'versions_done', 'versions_total') },
        badges: P.badges_partial === true,
    }
}
// подпись блока через t(); неизвестный ключ — сам ключ
export function pmHistory_partialBlockTitle(key, t = (s) => s) {
    const k = str(key)
    return PMH_PARTIAL_TITLES[k] ? t(PMH_PARTIAL_TITLES[k]) : k
}
// блок не дочитан?
export function pmHistory_blockPartial(payload, block) {
    return pmHistory_partial(payload).blocks.includes(str(block))
}

// IE 2026 10 29 / ODOO-5800 (ie-dm7 #114932: история fast → deep, стадии ответа)
// СТАДИИ ОТВЕТА (BE params.history.stage, pm_history.py «БЫСТРЫЙ СЛОЙ»): fast — быстрый слой (пара по умолчанию + lite, остальное только из кэша), deep — полный ответ;
// ответ несёт stage ('fast' | 'deep' | 'full'), deep_ready, deep_pending[], lite_why_partial, days[].pending. Фронт: первый запрос и каждый перезапрос — fast; fast с deep_ready=false →
// сразу тот же запрос со stage=deep (ответ ЗАМЕНЯЕТ данные); deep с partial — автоповтор до PMH_STAGE_RETRIES раз. Нет поля stage (старый BE) — прежнее поведение.
// BE эхом возвращает только stage (ни request_id, ни номера запроса), поэтому устаревшие ответы отбрасывает фронт: счётчик поколения (gen) растёт с каждым запросом пользователя,
// список отправленных запросов { gen, stage } сверяется с стадией пришедшего ответа (pmHistory_stageTake).
export const PMH_STAGE_RETRIES = 5
// выключатель fe.ux.pmHistoryStaged (по умолчанию вкл; false — запрос без stage, как раньше). setting — (ключ, умолчание) → значение
export function pmHistory_stagedOn(setting) {
    try { return typeof setting !== 'function' || setting('fe.ux.pmHistoryStaged', true) !== false } catch (e) { return true }
}
// стадия ответа: 'fast' | 'deep' | 'full' | '' (нет поля — старый BE)
export function pmHistory_stageOf(payload) {
    const s = isObj(payload) ? str(payload.stage).toLowerCase() : ''
    return s === 'fast' || s === 'deep' || s === 'full' ? s : ''
}
// состояние стадий для шаблона: staged — ответ несёт stage; deepWait — быстрый слой, глубокий ещё считается; deepPending — блоки, не посчитанные стадией; liteWhyPartial — причины лайта неполные
export function pmHistory_stageInfo(payload) {
    const P = isObj(payload) ? payload : {}
    const stage = pmHistory_stageOf(P)
    const pend = (Array.isArray(P.deep_pending) ? P.deep_pending : []).map(str).filter(Boolean)
    return {
        staged: stage !== '', stage,
        deepReady: stage !== '' && P.deep_ready !== false,
        deepWait: stage === 'fast' && (P.deep_ready === false || pend.length > 0),
        deepPending: pend,
        liteWhyPartial: stage !== '' && P.lite_why_partial === true,
    }
}
// день ряда, чья сводка ещё не посчитана (days[].pending = true) — «…», не ошибка
export function pmHistory_dayPending(day) {
    return isObj(day) && day.pending === true && !str(day.error)
}
// стадия запроса пользователя: выключено / не v2 → '' (stage не шлём); «Read more» над deep с partial → deep (продолжить, не откатываться на fast); иначе fast
export function pmHistory_userStage(payload, o = {}) {
    if (!o.staged) return ''
    if (o.readMore && pmHistory_stageOf(payload) === 'deep' && pmHistory_partial(payload).partial) return 'deep'
    return 'fast'
}
// сверка пришедшего ответа со списком отправленных запросов inflight [{ gen, stage }] и поколением gen (последний запрос пользователя).
// Запись — последняя с той же стадией (нет такой — последняя вообще: BE выключил стадии / старый BE); убираются она и более ранние записи той же стадии
// (потерянные ответы не залипают). verdict: 'stale' — запрос старше поколения (ответ не применять), 'apply' — применить, 'foreign' — запроса нет в списке
// (ответ на ленивую загрузку вкладки) — применять. → { verdict, entry, inflight }
export function pmHistory_stageTake(inflight, payload, gen) {
    const list = Array.isArray(inflight) ? inflight.filter(isObj) : []
    const stage = pmHistory_stageOf(payload)
    let i = -1
    for (let k = list.length - 1; k >= 0; k--) if (str(list[k].stage) === stage || (stage === 'full' && str(list[k].stage) === '')) { i = k; break }
    if (i < 0) i = list.length - 1
    if (i < 0) return { verdict: 'foreign', entry: null, inflight: [] }
    const entry = list[i]
    const same = str(entry.stage)
    const rest = list.filter((x, k) => k > i || (k < i && str(x.stage) !== same))
    return { verdict: num(entry.gen) < num(gen) ? 'stale' : 'apply', entry, inflight: rest }
}
// следующий запрос после применённого ответа → { stage: 'deep', retries } | null. fast с deep_ready=false → deep (повторы с нуля); deep с partial → ещё deep, пока повторов < PMH_STAGE_RETRIES.
// Ответ с ошибкой (state != ok) дальше не тянем; выключатель выкл / нет stage — null
export function pmHistory_stageNext(payload, o = {}) {
    if (!o.staged || !isObj(payload)) return null
    const stage = pmHistory_stageOf(payload)
    if (!stage || str(payload.state).startsWith('error')) return null   // IE 2026 10 29 / ODOO-5800 (webscm #115244: BE шлёт state 'error: …' — точное сравнение не срабатывало, deep с ошибкой тянулся до PMH_STAGE_RETRIES раз)
    const retries = num(o.retries)
    if (stage === 'fast' && payload.deep_ready === false) return { stage: 'deep', retries: 0 }
    if (stage === 'deep' && pmHistory_partial(payload).partial && retries < PMH_STAGE_RETRIES) return { stage: 'deep', retries: retries + 1 }
    return null
}

// ===================== IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other) =====================
// РЕЖИМ «ПРАВИЛА» ИЗ ДОКУМЕНТОВ РАСЧЁТА (BE out.rules.source === 'calc'): ряд точек (архивы ПМ + текущий расчёт), карточки по видам карт (segment),
// таблицы participating / other, срез текущих записей MAP-* (current_slice). source 'audit' или нет ключа — прежний вид (pmHistory_rules).
// Выключатель фронта — fe.ux.pmHistoryRulesCalc (по умолчанию вкл; выкл — прежний вид и при source 'calc').
export function pmHistory_rulesCalcOn(setting) {   // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other)
    try { return typeof setting !== 'function' || setting('fe.ux.pmHistoryRulesCalc', true) !== false } catch (e) { return true }
}
const rcBool = (v) => (typeof v === 'boolean' ? v : null)   // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other)
const rcOp = (v) => { const o = str(v).toUpperCase(); return o === 'I' || o === 'U' || o === 'D' ? o : '' }   // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other)
const rcVal = (v) => (v == null ? '' : isObj(v) || Array.isArray(v) ? JSON.stringify(v) : String(v))   // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other)
const rcInherited = (v) => (isObj(v) ? Object.keys(v).map(f => ({ f, v: rcVal(v[f]) })) : [])   // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other)
const rcKind = (v) => (str(v) === 'group' ? 'group' : 'rule')   // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other)
const RC_COUNTS = ['rules', 'participating', 'other', 'used', 'nodes_extra', 'txn_rows', 'groups']   // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other)
const RC_COLORS = 6   // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other): цветов видов карт (pmh-rc-c0…c5)
// правило списка participating[] / other[] (снимок точки «to»)
function rcRule(r, group) {   // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other)
    return {
        id: str(r.id), segment: str(r.segment), title: str(r.title), object: str(r.object_title), object_id: str(r.object), subject_from: str(r.subject_from), subject_to: str(r.subject_to), subject_from_title: str(r.subject_from_title), subject_to_title: str(r.subject_to_title),
        used_in_calc: rcBool(r.used_in_calc), active: rcBool(r.active), nodes_count: numOrNull(r.nodes_count), txn_rows: numOrNull(r.txn_rows),
        fields: isObj(r.fields) ? r.fields : {}, updated_by: str(r.updated_by), updated_at: str(r.updated_at), op: rcOp(r.op), kind: rcKind(r.kind),
        inherited: rcInherited(r.inherited), fields_source: str(r.fields_source), group_rules: numOrNull(r.group_rules), group,
    }
}
// строка изменения пары (rows / changes[].rows)
function rcRow(r) {   // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other)
    return {
        id: str(r.id), segment: str(r.segment), title: str(r.title), object: str(r.object_title), object_id: str(r.object), subject_from: str(r.subject_from), subject_to: str(r.subject_to), subject_from_title: str(r.subject_from_title), subject_to_title: str(r.subject_to_title),
        // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119691: fields_titles {f: {from_title, to_title}} — титулы значений-ссылок, когда BE их нашёл; нет — значение как есть)
        op: rcOp(r.op), fields: (Array.isArray(r.fields) ? r.fields : []).filter(isObj).map(f => { const T = isObj(r.fields_titles) && isObj(r.fields_titles[str(f.f)]) ? r.fields_titles[str(f.f)] : {}; return { f: str(f.f), from: rcVal(f.from), to: rcVal(f.to), from_title: str(T.from_title), to_title: str(T.to_title) } }),
        by: str(r.by), at: str(r.at), signal: r.signal === true, group: str(r.group), used_from: rcBool(r.used_from), used_to: rcBool(r.used_to), used_chg: r.used_chg === true,
        nodes_count: numOrNull(r.nodes_count), nodes_from: numOrNull(r.nodes_from), nodes_to: numOrNull(r.nodes_to), who_source: str(r.who_source), kind: rcKind(r.kind),
        inherited: rcInherited(r.inherited), fields_source: str(r.fields_source),
    }
}
function rcWhoSkipped(w) {   // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other)
    if (!isObj(w)) return null
    return { reason: str(w.reason), days: numOrNull(w.days), ids: numOrNull(w.ids) }
}
function rcGroupCounts(g) {   // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other)
    const G = isObj(g) ? g : {}
    return { added: num(G.added), changed: num(G.changed), removed: num(G.removed), used_chg: num(G.used_chg) }
}
// → null (не calc) | { source, pair_source, points, changes, by_group, cards, segments, participating, other, rows_part, rows_other, totals, to, current_slice, who_skipped, rows_limited, total }
export function pmHistory_rulesCalc(payload) {   // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other)
    const R = isObj(payload) && isObj(payload.rules) ? payload.rules : null
    if (!R || str(R.source) !== 'calc') return null
    const participating = (Array.isArray(R.participating) ? R.participating : []).filter(isObj).map(r => rcRule(r, 'participating'))
    const other = (Array.isArray(R.other) ? R.other : []).filter(isObj).map(r => rcRule(r, 'other'))
    const byId = {}
    for (const r of other.concat(participating)) if (r.id) byId[r.id] = r   // participating поверх other
    // виды карт: порядок — participating, other, строки; цвет — индекс по кругу
    const segments = []
    const addSeg = (s) => { if (s && !segments.includes(s)) segments.push(s) }
    const rows = (Array.isArray(R.rows) ? R.rows : []).filter(isObj).map(rcRow)
    const BS = isObj(R.by_segment) ? R.by_segment : {}
    for (const r of participating.concat(other, rows)) addSeg(r.segment)
    for (const k of Object.keys(BS)) if (isObj(BS[k])) addSeg(k)
    const ci = (s) => Math.max(0, segments.indexOf(s)) % RC_COLORS
    // строки таблиц: изменения пары своей группы; группа строки не пришла — по спискам; поля списка (титул, used_in_calc, узлы) — дополнением
    const line = (r) => {
        const L = byId[r.id] || null
        const used = r.used_to != null ? r.used_to : (L ? L.used_in_calc : null)
        return { ...r, title: r.title || (L ? L.title : ''), object: r.object || (L ? L.object : ''),
            subject_from_title: r.subject_from_title || (L ? L.subject_from_title : ''), subject_to_title: r.subject_to_title || (L ? L.subject_to_title : ''), group: r.group || (L ? L.group : 'other'), ci: ci(r.segment),
            used_in_calc: used, nodes_count: r.nodes_count != null ? r.nodes_count : (L ? L.nodes_count : null), group_rules: L ? L.group_rules : null,
            by: r.by || (L ? L.updated_by : ''), at: r.at || (L ? L.updated_at : '') }
    }
    let lines = rows.map(line)
    // строк пары нет (усечены / не пришли), а в списках есть изменённые — показать их (без полей)
    if (!lines.length) lines = participating.concat(other).filter(r => r.op).map(L => line({ ...rcRow({}), id: L.id, segment: L.segment, op: L.op, kind: L.kind, inherited: [], who_source: 'calc', group: L.group }))
    // карточки по видам карт: кольцо и «участь / иные» — из показанных participating[] + other[] (BE усекает списки до rows_max: in_calc = показано, не всё в расчёте);
    // полные счётчики изменений вида карт — by_segment (added / changed / archived / objects; нет записи — null)
    const cards = segments.map(s => {
        const P = participating.filter(r => r.segment === s), O = other.filter(r => r.segment === s), B = isObj(BS[s]) ? BS[s] : null
        const removed = P.concat(O).filter(r => r.op === 'D').length
        const changed_part = P.filter(r => r.op === 'I' || r.op === 'U').length, changed_other = O.filter(r => r.op === 'I' || r.op === 'U').length
        const in_calc = P.length + O.length
        const bs = B ? { added: num(B.added), changed: num(B.changed), archived: num(B.archived), objects: num(B.objects) } : null
        // полные счётчики точки «to» по виду карт (BE by_segment[s].calc) — есть: карточка из них (full); нет — из показанных списков
        const F = B && isObj(B.calc) ? B.calc : null
        if (F) {
            const c = { in_calc: num(F.in_calc), participating: num(F.participating), other: num(F.other), changed_part: num(F.changed_part), changed_other: num(F.changed_other), removed: num(F.removed), groups: numOrNull(F.groups), used: numOrNull(F.used) }
            const changed = c.changed_part + c.changed_other + c.removed > 0 || bs.added + bs.changed + bs.archived > 0
            return { segment: s, ci: ci(s), ...c, unchanged: Math.max(0, c.in_calc - c.changed_part - c.changed_other - c.removed), by_segment: bs, changed, full: true }
        }
        const changed = changed_part + changed_other + removed > 0 || !!(bs && bs.added + bs.changed + bs.archived > 0)
        return { segment: s, ci: ci(s), in_calc, participating: P.length, other: O.length, changed_part, changed_other, removed, unchanged: Math.max(0, in_calc - changed_part - changed_other - removed), groups: null, used: null, by_segment: bs, changed, full: false }
    }).filter(c => c.in_calc > 0 || c.by_segment)
    const cards_full = cards.length > 0 && cards.every(c => c.full)
    const changes = (Array.isArray(R.changes) ? R.changes : []).filter(isObj).map(c => ({
        from: str(c.from), to: str(c.to), from_date: str(c.from_date), to_date: str(c.to_date), source: str(c.source) || 'calc',
        added: numOrNull(c.added), changed: numOrNull(c.changed), removed: numOrNull(c.removed), used_chg: numOrNull(c.used_chg),
        rows_total: numOrNull(c.rows_total), who_skipped: rcWhoSkipped(c.who_skipped),
    }))
    const points = pmHistory_rcPoints(R.points, changes)
    const S = isObj(R.current_slice) ? R.current_slice : null
    const RS = S && isObj(S.rules) ? S.rules : {}
    const sliceRules = {}
    for (const id of Object.keys(RS)) {
        const x = RS[id]
        if (!isObj(x)) continue
        sliceRules[id] = { segment: str(x.segment), fields: isObj(x.fields) ? x.fields : {}, updated_at: str(x.updated_at), updated_by: str(x.updated_by), archived: x.archived === true, missing: x.missing === true,
            differs: x.differs_from_calc === true, diff: (Array.isArray(x.diff) ? x.diff : []).filter(isObj).map(f => ({ f: str(f.f), from: rcVal(f.from), to: rcVal(f.to) })) }
    }
    const current_slice = S ? { total: num(S.total), differs: num(S.differs), missing: num(S.missing), archived: num(S.archived), rules: sliceRules } : null
    const T = isObj(R.to) ? R.to : {}
    const to = {}
    for (const k of RC_COUNTS) to[k] = numOrNull(T[k])
    const BG = isObj(R.by_group) ? R.by_group : {}
    return {
        source: 'calc', pair_source: str(R.pair_source) || 'calc', points, changes,
        by_group: { participating: rcGroupCounts(BG.participating), other: rcGroupCounts(BG.other) },
        cards, cards_full, segments, participating, other,
        rows_part: lines.filter(r => r.group === 'participating'), rows_other: lines.filter(r => r.group !== 'participating'),
        totals: { participating_total: numOrNull(R.participating_total), other_total: numOrNull(R.other_total), groups_total: numOrNull(R.groups_total) },
        lists_limited: (numOrNull(R.participating_total) != null && numOrNull(R.participating_total) > participating.length) || (numOrNull(R.other_total) != null && numOrNull(R.other_total) > other.length),
        to, current_slice, who_skipped: rcWhoSkipped(R.who_skipped), rows_limited: R.rows_limited === true, total: numOrNull(R.total),
    }
}
// ТОЧКИ РЯДА: points[] + интервал, который кончается в точке (changes[].to = id точки, иначе to_date = дата): n = added + changed + removed, размер пузырька 16…30 px
export function pmHistory_rcPoints(points, changes) {   // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other)
    const C = Array.isArray(changes) ? changes : []
    const out = (Array.isArray(points) ? points : []).filter(isObj).map(p => {
        const id = str(p.id), date = str(p.date)
        const counts = {}
        for (const k of RC_COUNTS) counts[k] = numOrNull(p[k])
        const c = C.find(x => isObj(x) && id && str(x.to) === id) || C.find(x => isObj(x) && date && str(x.to_date) === date) || null
        const has = c && [c.added, c.changed, c.removed].some(v => v != null)
        const interval = c ? { source: str(c.source) || 'calc', n: has ? num(c.added) + num(c.changed) + num(c.removed) : null, used_chg: numOrNull(c.used_chg) } : null
        return { id, date, version: str(p.version), kind: str(p.kind), current: p.current === true, source: str(p.source) || 'calc', counts, interval, size: 0 }
    })
    const max = out.reduce((m, p) => Math.max(m, p.interval && p.interval.n ? p.interval.n : 0), 0)
    for (const p of out) if (p.interval && p.interval.n) p.size = Math.round(16 + 14 * Math.sqrt(p.interval.n / (max || 1)))
    return out
}
// КОЛЬЦО карточки (SVG, окружность r): дуги changed_part / changed_other / removed / unchanged → [{ key, n, dash: 'len gap', offset }] (нулевые — пропуск)
export function pmHistory_rcRing(card, r = 26) {   // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other)
    const C = 2 * Math.PI * r, K = isObj(card) ? card : {}
    const parts = ['changed_part', 'changed_other', 'removed', 'unchanged'].map(key => ({ key, n: num(K[key]) })).filter(p => p.n > 0)
    const tot = parts.reduce((a, p) => a + p.n, 0)
    if (!tot) return []
    let off = 0
    return parts.map(p => {
        const len = C * p.n / tot, gap = parts.length > 1 ? Math.min(2, len / 2) : 0
        const a = { key: p.key, n: p.n, dash: (len - gap).toFixed(2) + ' ' + (C - len + gap).toFixed(2), offset: (-off).toFixed(2) }
        off += len
        return a
    })
}
// «поле: было → стало; …» (пустое — «—»)
// значение-ссылка с титулом (from_title / to_title, ie-dm7 #119691) — показывается титул, не id
export function pmHistory_rcFieldsText(fields) {   // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other)
    const v = (val, title) => str(title) || str(val) || '—'
    return (Array.isArray(fields) ? fields : []).filter(isObj).map(f => str(f.f) + ': ' + v(f.from, f.from_title) + ' → ' + v(f.to, f.to_title)).join('; ')
}
// наследование строкой: группа — «поле: n rules», правило — «поле: group <id>»; t(key, params) — перевод (по умолчанию подстановка)
const rcT = (s, p) => String(s).replace(/\{(\w+)\}/g, (m, k) => (p && p[k] != null ? String(p[k]) : m))   // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other)
// titles — { <id группы>: title } (строки kind 'group' того же ответа): у правила вместо id группы — её титул, когда он есть
export function pmHistory_rcInheritedText(inherited, kind, t = rcT, titles = null) {   // IE 2026 10 31 / ODOO-5800 (ie-dm7 #119671: rules from calc — points, cards, participating / other)
    const L = Array.isArray(inherited) ? inherited : rcInherited(inherited)
    const name = id => (isObj(titles) && str(titles[id]) ? str(titles[id]) : id)
    return L.filter(x => isObj(x) && str(x.f)).map(x => (str(kind) === 'group' ? t('{f}: {n} rules', { f: str(x.f), n: str(x.v) }) : t('{f}: from group {id}', { f: str(x.f), id: name(str(x.v)) }))).join(' · ')
}
